import React, { useContext, useState } from 'react';
import { Pressable, Text } from 'react-native';
import { useQuery, useMutation } from '@apollo/client';
import DataTable from '@preflighttech/preflight-tables';
import { DateTime } from 'luxon';
import Print from '../../../elements/Print';

import DataState from 'app/src/components/DataState';
import AuthContext from 'app/src/contexts/AuthContext';
import {
  MainContainer,
  ContentContainer,
  PageTitle,
  LinkContainer,
  LinkSpace,
} from 'app/src/elements';
import Modal from 'app/src/elements/Modal';
import { currency } from 'app/src/utils/stringHelpers';

import { RECONCILES, HIDE_RECONCILE, UPDATE_RECONCILE } from './queries';
import SubNav from './SubNav';
import EditReconcile from './edit';
import Perform from './Perform';

const checkedColumn = ({ value }) => value ? '✓' : '';

const baseColumns = [
  {
    key: 'account',
    label: 'Account',
    value: ({ value }) => value?.fullName,
  },
  {
    key: 'startOn',
    label: 'Start Date',
    sort: 'desc',
    content: ({ value }) => (
      DateTime.fromISO(value).toLocaleString(DateTime.DATE_SHORT)
    ),
  },
  {
    key: 'endOn',
    label: 'End Date',
    content: ({ value }) => (
      DateTime.fromISO(value).toLocaleString(DateTime.DATE_SHORT)
    ),
  },
  {
    key: 'startBalance',
    label: 'Start Balance',
    content: ({ value }) => currency(value),
  },
  {
    key: 'endBalance',
    label: 'End Balance',
    content: ({ value }) => currency(value),
  },
  {
    key: 'completedAt',
    label: 'Completed',
    content: checkedColumn,
  },
];

const Reconciles = () => {
  const [showEditModal, setShowEditModal] = useState();
  const [showPerformModal, setShowPerformModal] = useState();

  const { data, refetch } = useQuery(RECONCILES);

  const [updateReconcile] = useMutation(UPDATE_RECONCILE, {
    onCompleted: (response) => {
      refetch();
      setShowPerformModal(response.updateReconcile.reconcile);
    }
  });

  if (!data) { return <DataState.Loading /> };

  const columns = [
    ...baseColumns,
    {
      key: 'functions',
      label: 'Functions',
      sort: 'prevent',
      content: ({ entry }) => (
        <LinkContainer>
          {!entry.completedAt ?
            <Pressable onPress={() => setShowPerformModal(entry)}>
              <Text>Perform</Text>
            </Pressable> :
            <Pressable onPress={() => {
              if(window.confirm(`Reopen ${entry.account.fullName} Reconcile?`)) {
                let variables = {...entry};
                variables['completedAt'] = null;
                variables['accountId'] = entry.account.id;
                variables['serviceChargeAmount'] = null;
                variables['serviceChargeDate'] = null;
                variables['serviceChargeAccountId'] = null;
                variables['interestAmount'] = null;
                variables['interestDate'] = null;
                variables['interestAccountId'] = null;
                variables['reconciledById'] = null;
                updateReconcile({variables: variables})
              }
            }}>
              <Text>
                Reopen
              </Text>
            </Pressable>
          }
          <span style={{width: '10px', textAlign: 'center'}}>{'|'}</span>
          <Pressable onPress={() => setShowEditModal(entry)}>
            <Text>Edit</Text>
          </Pressable>
          {entry.completedAt ?
            <>
              <span style={{width: '10px', textAlign: 'center'}}>{'|'}</span>
              <Print
                path={`reports/reconcile-completed/${entry.id}`}
                filename={`${entry.account.name}-${entry.endOn}-report`}
              />
            </> :
            null
          }
        </LinkContainer>
      )
    },
  ];

  return (
    <MainContainer>
      <SubNav refetch={refetch} selects={data} />

      <ContentContainer>
        <PageTitle>Reconcile Accounts</PageTitle>

        <DataTable.Simple
          data={data.reconciles}
          columns={columns}
        />
      </ContentContainer>

      {showEditModal &&
        <Modal
          showModal
          setShowModal={setShowEditModal}
          modalType="Edit Reconcile"
        >
          <EditReconcile
            reconcile={showEditModal}
            setShowModal={setShowEditModal}
            refetch={refetch}
            selects={data}
          />
        </Modal>
      }
      {showPerformModal &&
        <Modal
          showModal
          setShowModal={setShowPerformModal}
          modalType="Perform Reconcile"
        >
          <Perform
            reconcile={showPerformModal}
            setShowModal={setShowPerformModal}
          />
        </Modal>
      }
    </MainContainer>
  );
};

export default Reconciles;
